import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  lastValueFrom,
  Observable,
  retry,
  timer,
  timeout,
  TimeoutError,
} from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export abstract class CrudService<T> {
  protected abstract basePath: string; // To be defined by extending services
  abstract instanceType: string; // Useful for logging or additional logic
  readonly userStore = inject(ActiveUserStore);
  constructor(protected http: HttpClient) {}

  /**
   * Retrieves the base URL for the API endpoint based on the environment and instance type.
   *
   * @return {string} The constructed base URL string.
   */
  protected getBaseUrl(): string {
    return `${environment.apiUrl}/api/${this.instanceType}`;
  }

  /**
   * Adds a bounded timeout and a couple of backed-off retries around a read
   * request, but only for transient failures (timeout / offline / 5xx).
   * 401/403 and other client errors are rethrown immediately so
   * AuthInterceptor stays the single source of truth for session expiry.
   */
  private withResilience<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      timeout(15_000),
      retry({
        count: 2,
        delay: (error, retryCount) => {
          const status =
            error instanceof HttpErrorResponse ? error.status : undefined;
          const isTransient =
            error instanceof TimeoutError ||
            status === 0 ||
            (status !== undefined && status >= 500);
          if (!isTransient) throw error;
          return timer(500 * Math.pow(2, retryCount - 1));
        },
      })
    );
  }

  /**
   * Retrieves an item from the server based on the provided identifier.
   *
   * @param {string} id - The unique identifier of the item to retrieve.
   * @return {Promise<T>} A promise that resolves to the retrieved item of type T.
   */
  getItem(id: string) {
    return lastValueFrom(this.http.get<T>(`${this.getBaseUrl()}/${id}`));
  }

  /**
   * Adds an item to the server by making a POST request.
   *
   * @param {T} item - The item to be added.
   * @return {Observable<T>} An observable containing the added item as returned by the server.
   */
  addItem(item: T): Observable<T> {
    return this.http.post<T>(`${this.getBaseUrl()}/insert`, item);
  }

  /**
   * Updates an existing item on the server based on its unique identifier.
   *
   * @param {T & { _id?: string }} item The item to be updated with an optional unique identifier (_id).
   * @return {Observable<T>} An observable representing the updated item from the server.
   */
  updateItem(item: T & { _id?: string }): Observable<T> {
    return this.http.put<T>(`${this.getBaseUrl()}/update/${item._id}`, item);
  }

  /**
   * Updates multiple items by sending a PUT request to the designated endpoint.
   *
   * @param {T[]} items - The array of items to be updated.
   * @return {Observable<T>} An observable that resolves to the updated items.
   */
  updateMultiple(items: T[]): Observable<T> {
    return this.http.put<T>(`${this.getBaseUrl()}/updateMany`, items);
  }

  /**
   * Inserts multiple items into the data source.
   *
   * @param {T[]} items - An array of items to be inserted.
   * @return {Observable<T>} An observable that emits the response of the insert operation.
   */
  insertMultiple(items: T[]): Observable<T> {
    return this.http.post<T>(`${this.getBaseUrl()}/insertMany`, items);
  }

  /**
   * Removes an item using its unique identifier and updates the backend.
   *
   * @param {T & { _id?: string }} item The item to be removed. Must contain an optional `_id` property representing its unique identifier.
   * @return {Observable<void>} An observable that completes when the item is successfully removed.
   */
  removeItem(item: T & { _id?: string }): Observable<void> {
    return this.http.put<void>(`${this.getBaseUrl()}/remove/${item._id}`, item);
  }

  /**
   * Removes multiple items by their IDs.
   *
   * @param {Array} items - An array of items to be removed. Each item must have an `_id` property of type string.
   * @return {Observable<string[] | void>} An observable that emits an array of removed item IDs or void if no response data is present.
   */
  removeMultiple(items: (T & { _id: string })[]): Observable<string[] | void> {
    return this.http.put<string[] | void>(
      `${this.getBaseUrl()}/removeMany`,
      items.map(item => item._id)
    );
  }

  /**
   * Fetches items as a promise from a specified endpoint based on the current user's commune.
   *
   * @return {Promise<{ [key: string]: T }>} A promise resolving to an object where keys are strings and values are of type T.
   */
  getItemsAsPromise(): Promise<{ [key: string]: T }> {
    return lastValueFrom(
      this.withResilience(
        this.http.get<{ [key: string]: T }>(
          `${this.getBaseUrl()}/commune/${this.userStore.commune()}`
        )
      )
    );
  }
  /**
   * Builds the upload URL for a given upload endpoint of this instance type.
   *
   * @param {string} endpoint - The upload route name (e.g. `uploadImage`, `uploadProfile`).
   * @return {string} The full upload URL.
   */
  protected buildUploadUrl(endpoint: string): string {
    return `${this.getBaseUrl()}/${endpoint}/`;
  }

  /**
   * Removes a previously uploaded file from the given upload folder.
   *
   * @param {string} folder - The upload subfolder the file lives in (e.g. `imagePool`, `profile`).
   * @param {string} filename - The name of the file to remove.
   * @param {string} communeId - The commune the file belongs to.
   */
  protected removeUploadedFile(
    folder: string,
    filename: string,
    communeId: string
  ) {
    return this.http
      .post(`${this.getBaseUrl()}/removeImage/`, {
        path: `uploads/${folder}/${communeId}/${filename}`,
      })
      .subscribe();
  }

  /**
   * Fetches items as a promise from a specified endpoint based on the current user's commune.
   *
   * @return {Promise<{ [key: string]: T }>} A promise resolving to an object where keys are strings and values are of type T.
   */
  getItemsByIds(ids: string[]): Promise<{ [key: string]: T }> {
    const headers = new HttpHeaders({
      'X-Ids': JSON.stringify(ids),
    });
    return lastValueFrom(
      this.withResilience(
        this.http.get<{ [key: string]: T }>(`${this.getBaseUrl()}/findByIds`, {
          headers,
        })
      )
    );
  }
}
